import { useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";

/** Natives `<dialog>` modal öffnen und schließen, gesteuert über `offen` – vor dem Zeichnen, damit nichts aufblitzt. */
export function useDialog(offen: boolean) {
  const ref = useRef<HTMLDialogElement>(null);
  useLayoutEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (offen && !d.open) d.showModal();
    if (!offen && d.open) d.close();
  }, [offen]);
  return ref;
}

type Props = {
  offen: boolean;
  titel: string;
  text?: ReactNode;
  /** Beschriftung des Bestätigungsknopfs, z. B. „Archivieren“. */
  bestaetigen: string;
  /** Rot statt Akzentfarbe – für Unumkehrbares. */
  gefaehrlich?: boolean;
  onBestaetigen(): void | Promise<void>;
  onAbbrechen(): void;
};

/**
 * Rückfrage vor einer Aktion als natives `<dialog>`. Escape, Klick daneben und „Abbrechen“
 * brechen ab; solange die Aktion läuft, bleibt der Dialog stehen.
 */
export function Bestaetigen({ offen, titel, text, bestaetigen, gefaehrlich, onBestaetigen, onAbbrechen }: Props) {
  const ref = useDialog(offen);
  const titelId = useId();
  const [laeuft, setLaeuft] = useState(false);

  async function ausfuehren() {
    setLaeuft(true);
    try {
      await onBestaetigen();
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <dialog
      ref={ref}
      className="stoffe-dialog"
      aria-labelledby={titelId}
      onCancel={(e) => {
        e.preventDefault();
        if (!laeuft) onAbbrechen();
      }}
      // Schließt der Browser den Dialog doch selbst (wiederholtes Escape), bleibt der Zustand stimmig.
      onClose={() => {
        if (offen) onAbbrechen();
      }}
      onClick={(e) => {
        // Klick auf den abgedunkelten Rand trifft das <dialog> selbst.
        if (e.target === e.currentTarget && !laeuft) onAbbrechen();
      }}
    >
      <div className="stoffe-dialog__inhalt">
        <h2 id={titelId}>{titel}</h2>
        {text && <p>{text}</p>}
        <div className="actions stoffe-dialog__aktionen">
          {/* Steht zuerst: showModal() fokussiert ihn, Enter bricht also nicht versehentlich etwas Unumkehrbares los. */}
          <button type="button" disabled={laeuft} onClick={onAbbrechen}>
            Abbrechen
          </button>
          <button
            type="button"
            className={gefaehrlich ? "stoffe-knopf--gefahr" : "primary"}
            disabled={laeuft}
            onClick={() => void ausfuehren()}
          >
            {bestaetigen}
          </button>
        </div>
      </div>
    </dialog>
  );
}
